import React from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@hqo/ui-atoms';
import { Spinner } from 'app/components/ui/spinner';
import { CheckIcon } from 'lucide-react';
import { cn } from '@hqo/react-hooks';

export interface UserDialogButton {
  title: string;
  variant?: 'default' | 'secondary';
  isLoading?: boolean;
  handler: VoidFunction;
}

export interface CustomSizes {
  width?: string;
  height?: string;
  paddings?: Array<string>;
}

export interface Props {
  isVisible: boolean;
  title: string;
  description?: string;
  buttons: Array<UserDialogButton>;
  customSizes?: CustomSizes;
  onClose: VoidFunction;
  errorMessage?: string;
  isConfirmation?: boolean;
}

/**
 * Bottom sheet for the attachable CTA flows (RSVP / claim / cancel) on mobile.
 *
 * Sized by its content, never by a fixed px height: Android WebViews apply the system
 * font size as text zoom, which grows the type but not px boxes — the old fixed
 * `h-[300px]` sheet let the title + description push the actions off-screen.
 * The sheet is pinned to the bottom of the viewport and capped at 90dvh; the copy
 * scrolls inside it while the actions stay pinned in a footer that never shrinks.
 */
export const MobileUserDialogModal: React.FC<Props> = ({ isVisible, title, description, buttons, onClose, errorMessage, isConfirmation }) => (
  <Dialog data-testid="dialog" open={isVisible} onOpenChange={onClose}>
    <DialogContent className={cn(
      'fixed inset-x-0 top-auto bottom-0 translate-x-0 translate-y-0',
      'flex flex-col gap-0 w-full max-w-full sm:max-w-full p-0 border-none rounded-t-2xl rounded-b-none',
      // Keeps the original 300px sheet at default text size; grows with the copy up to 90% of
      // the viewport (dvh where supported so mobile browser chrome can't cover the actions).
      'min-h-[min(300px,90vh)] supports-[height:100dvh]:min-h-[min(300px,90dvh)]',
      'max-h-[90vh] supports-[height:100dvh]:max-h-[90dvh] overflow-y-auto overflow-x-hidden',
      'data-[state=open]:fade-in-100 data-[state=closed]:fade-out-100',
      'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
      'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-500',
      )}
    >
      <div data-testid="dialog-body" className="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto overflow-x-hidden overscroll-contain px-7 pt-7">
        {isConfirmation && (
          <div className="flex justify-center items-center">
            <CheckIcon data-testid="check-icon" strokeWidth={3} className="w-12 h-12 text-system-green bg-system-green-light rounded-full p-3" />
          </div>
        )}
        <DialogHeader className="text-center w-full pt-2">
          <DialogTitle className="font-semibold text-2xl leading-6 text-system-black">
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="font-normal text-lg text-center leading-6 text-system-grey-dark">
          {description && <span>{description}</span>}
        </DialogDescription>
      </div>
      <div
        data-testid="dialog-actions"
        className="flex flex-col shrink-0 gap-2 px-7 pt-4 pb-7"
      >
        {errorMessage && <p className="flex flex-col justify-end text-start text-system-pink text-[14px] min-h-12 w-full">{errorMessage}</p>}
        <DialogFooter className="gap-3">
          {buttons.map(({ title: buttonTitle, handler, variant, isLoading }) => (
            <Button
              key={buttonTitle}
              disabled={isLoading}
              onClick={handler}
              variant={variant || 'default'}
              className={`min-h-14 h-auto whitespace-normal text-lg min-w-[100px] ${
            variant === 'secondary'
              ? 'bg-white border border-gray-600 text-gray-600 hover:bg-gray-100'
              : 'bg-primary border border-primary text-white hover:bg-primary focus:outline-primary active:outline-primary'
          }`}
            >
              {isLoading ? <Spinner data-testid="spinner" size="small" /> : buttonTitle}
            </Button>
          ))}
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
);
